"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { X, CheckCircle2, AlertTriangle, XCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

/* ── Types ──────────────────────────────────────────────────── */

type ToastVariant = "success" | "error" | "warning" | "info";

interface ToastData {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextValue {
  toast: (opts: Omit<ToastData, "id">) => void;
}

/* ── Context ────────────────────────────────────────────────── */

const ToastContext = createContext<ToastContextValue>({
  toast: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

/* ── Provider ───────────────────────────────────────────────── */

const DEFAULT_DURATION = 5000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const toast = useCallback((opts: Omit<ToastData, "id">) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((prev) => [...prev, { ...opts, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container — bottom-right.
          Wrapped in AnimatePresence so dismissed toasts slide out + fade
          instead of vanishing instantly. `popLayout` keeps the remaining
          toasts in place during exit so the stack settles smoothly when
          a middle item is dismissed. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-[var(--space-4)] left-[var(--space-4)] right-[var(--space-4)] z-[var(--z-toast)] flex flex-col gap-[var(--space-2)] w-auto max-w-sm ml-auto pointer-events-none"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

/* ── Toast item ─────────────────────────────────────────────── */

const VARIANT_STYLES: Record<
  ToastVariant,
  { icon: React.ElementType; borderClass: string; iconClass: string }
> = {
  success: {
    icon: CheckCircle2,
    borderClass: "border-l-[var(--color-success)]",
    iconClass: "text-[var(--color-success)]",
  },
  error: {
    icon: XCircle,
    borderClass: "border-l-[var(--color-error)]",
    iconClass: "text-[var(--color-error)]",
  },
  warning: {
    icon: AlertTriangle,
    borderClass: "border-l-[var(--color-warning)]",
    iconClass: "text-[var(--color-warning)]",
  },
  info: {
    icon: Info,
    borderClass: "border-l-[var(--color-info)]",
    iconClass: "text-[var(--color-info)]",
  },
};

function ToastItem({
  toast: t,
  onDismiss,
}: {
  toast: ToastData;
  onDismiss: (id: string) => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { icon: Icon, borderClass, iconClass } = VARIANT_STYLES[t.variant];

  useEffect(() => {
    const duration = t.duration ?? DEFAULT_DURATION;
    timerRef.current = setTimeout(() => onDismiss(t.id), duration);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [t.id, t.duration, onDismiss]);

  return (
    <motion.div
      layout
      role="alert"
      initial={{ x: "calc(100% + var(--space-4))", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "calc(100% + var(--space-4))", opacity: 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 32 }}
      className={cn(
        "pointer-events-auto",
        "flex items-start gap-[var(--space-3)]",
        "rounded-[var(--radius-lg)] border border-[var(--color-border-default)]",
        "border-l-[3px]",
        borderClass,
        "bg-[var(--color-bg-elevated)] shadow-[var(--shadow-lg)]",
        "px-[var(--space-4)] py-[var(--space-3)]",
      )}
    >
      <Icon className={cn("size-5 shrink-0 mt-0.5", iconClass)} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-medium)] text-[var(--color-text-primary)]">
          {t.title}
        </p>
        {t.description && (
          <p className="mt-[var(--space-0-5)] text-[length:var(--font-size-xs)] text-[var(--color-text-muted)]">
            {t.description}
          </p>
        )}
      </div>
      <button
        onClick={() => onDismiss(t.id)}
        className="shrink-0 rounded-[var(--radius-sm)] p-[var(--space-0-5)] text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-[var(--color-primary-muted)]"
        aria-label="Dismiss"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </motion.div>
  );
}
